/**
 * Je gemiddelde door een trajectcontrole.
 *
 * ------------------------------------------------------------------
 * Dit is de derde vertaling van dezelfde rekenkern. Kotlin heeft hem
 * (data/SectionRun.kt), Swift heeft hem (MeldRadarKit/SectionRun.swift), en
 * hier staat hij voor de mini app. Dat is met opzet een vertaling en geen
 * nieuwe uitvinding: drie apps die een ander gemiddelde tonen voor dezelfde
 * meter asfalt is erger dan één app die het niet toont.
 *
 * Een trajectcontrole trekt zich niets aan van hoe hard je zijn camera
 * passeert. Hij deelt de lengte van het stuk door de tijd die je erover deed,
 * en dat is het enige getal dat iets beslist — dus het enige dat een
 * bestuurder hoort te zien. "Flitser over 400 meter" bij de eerste galg zegt
 * bij dit soort controle vrijwel niets.
 *
 * Bewust gemeten uit afstand en tijd, en niet door de snelheidsmeter te
 * middelen: dat is wat de handhaving zelf doet, en het middelen van
 * momentopnames zou meedrijven met elke GPS-hik.
 *
 * Zuiver, zodat de rekenkunde te controleren is zonder er een te rijden.
 */

const M_PER_DEG_LAT = 111320.0;

/** Kompaspeiling van a naar b, in graden. */
export function bearingDegrees(aLat, aLon, bLat, bLon) {
  const p1 = (aLat * Math.PI) / 180;
  const p2 = (bLat * Math.PI) / 180;
  const dl = ((bLon - aLon) * Math.PI) / 180;
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/**
 * De kortste afstand van een punt tot een polylijn, plus de peiling van het
 * stuk waar je op landt en hoeveel meter je dan al langs de lijn bent.
 *
 * Die peiling is wat een weg onderscheidt van de rijbaan ernaast die de
 * andere kant op loopt. Die offset is waar de hele trajectmodule op rust.
 *
 * `polyline` is [[lat, lon], ...]. Let op: de server levert `section` in
 * GeoJSON-volgorde ([lon, lat]) — zie sectionLine() hieronder.
 *
 * ------------------------------------------------------------------
 * **`venster` is er omdat het dichtstbijzijnde punt niet het juiste punt
 * hoeft te zijn.** Een trajectlijn kan langs zichzelf lopen: één OSM-relatie
 * met beide rijbanen, een ring, een knooppunt. Dan ligt een segment vierhonderd
 * meter verderop soms dichterbij dan het segment waar je werkelijk op rijdt, en
 * springt de offset in één GPS-tik honderden meters.
 *
 * Gemeten op 9 oktober op de echte lijn van `osm-rel-7219393-1`
 * ("Trajectcontrole A10 rechts · max 80", 48 punten). Die lijn gaat noord,
 * keert om, loopt via exact dezelfde coördinaten terug — beide rijbanen in één
 * lijn, een à twee meter uit elkaar — en gaat dan weer noord. Begin en eind
 * liggen 2.122 m uit elkaar terwijl de lijn 3.633 m lang is.
 *
 * Gevolg: op de terugweg projecteert elk punt op de heenweg, en de offset is
 * daar tot **1.091 meter** mis. Rijd je er met constante 80 km/u doorheen, dan
 * toont het scherm per GPS-tik 80, 70, 51, 42, 19, 18 en dan weer 80. Met dit
 * venster is het strak 80.
 *
 * De schermafdruk die dit aan het licht bracht zei "203 km/u gemiddeld" op
 * datzelfde traject. Dat is dezelfde soort fout — `travelledM` is
 * `|laatste − start|`, dus een misprojectie aan één van beide uiteinden tilt
 * het gemiddelde op of haalt het omlaag — maar dat exacte getal is hier niet
 * nagespeeld en wordt hier dus ook niet geclaimd.
 *
 * Met `{ offsetM, vooruitM, achteruitM }` tellen alleen segmenten mee waarvan
 * de offset binnen dat venster ligt. Het is asymmetrisch: vooruit zo ver als de
 * verstreken tijd toelaat, achteruit alleen de ruis. Zie sprongVenster.
 *
 * Null bij minder dan twee punten, of als er niets binnen het venster ligt.
 */
export function projectOnPolyline(lat, lon, polyline, venster = null) {
  if (!polyline || polyline.length < 2) return null;
  let best = Infinity;
  let bestBearing = 0;
  let bestOffset = 0;
  let travelled = 0;
  for (let i = 0; i < polyline.length - 1; i++) {
    const [aLat, aLon] = polyline[i];
    const [bLat, bLon] = polyline[i + 1];
    const mPerLon = M_PER_DEG_LAT * Math.cos((aLat * Math.PI) / 180);
    const vx = (bLon - aLon) * mPerLon;
    const vy = (bLat - aLat) * M_PER_DEG_LAT;
    const px = (lon - aLon) * mPerLon;
    const py = (lat - aLat) * M_PER_DEG_LAT;
    const lenSq = vx * vx + vy * vy;
    const t = lenSq === 0 ? 0 : Math.min(1, Math.max(0, (px * vx + py * vy) / lenSq));
    const dx = px - t * vx;
    const dy = py - t * vy;
    const d = Math.sqrt(dx * dx + dy * dy);
    const pieceLength = Math.sqrt(lenSq);
    const offset = travelled + t * pieceLength;
    // Buiten het venster telt dit stuk niet mee. Zonder dit wint het globaal
    // dichtstbijzijnde segment, en op een lijn die langs zichzelf loopt is dat
    // een ander deel van de weg. Zie de kop.
    const stap = offset - (venster ? venster.offsetM : 0);
    const inBeeld = !venster
      || (stap >= 0 ? stap <= venster.vooruitM : -stap <= venster.achteruitM);
    if (inBeeld && d < best) {
      best = d;
      bestBearing = bearingDegrees(aLat, aLon, bLat, bLon);
      bestOffset = offset;
    }
    travelled += pieceLength;
  }
  // Niets binnen het venster: we weten niet waar op deze lijn je zit. Dat is
  // een eerlijker antwoord dan een plek verzinnen.
  if (best === Infinity) return null;
  return { distanceM: best, bearing: bestBearing, offsetM: bestOffset };
}

/** De totale lengte van een polylijn in meters. */
export function polylineLength(polyline) {
  if (!polyline || polyline.length < 2) return 0;
  let total = 0;
  for (let i = 0; i < polyline.length - 1; i++) {
    const [aLat, aLon] = polyline[i];
    const [bLat, bLon] = polyline[i + 1];
    const mPerLon = M_PER_DEG_LAT * Math.cos((aLat * Math.PI) / 180);
    const dx = (bLon - aLon) * mPerLon;
    const dy = (bLat - aLat) * M_PER_DEG_LAT;
    total += Math.sqrt(dx * dx + dy * dy);
  }
  return total;
}

/**
 * De `section` van de server omgezet naar [[lat, lon], ...].
 *
 * De server slaat hem op in GeoJSON-volgorde, lon eerst. Dat is één keer
 * omdraaien op de grens en niet dertien keer in de rekenkunde — precies de
 * verwarring waar deze functie voor bestaat.
 */
export const sectionLine = (section) =>
  Array.isArray(section) && section.length > 1
    ? section.map(([lon, lat]) => [lat, lon])
    : null;

/** Hoe dicht bij de lijn je moet zitten om erop te tellen. Zelfde 60 m als Android en iOS. */
export const SECTION_ON_ROAD_M = 60;

/** Een paar bruikbare GPS-tikken voordat het eerste gemiddelde verschijnt. */
export const MIN_METRES = 50;
export const MIN_SECONDS = 3;

/** Een kort GPS-gat mag een lopende trajectmeting niet opnieuw laten beginnen. */
export const SECTION_EXIT_GRACE_MS = 15000;

/**
 * Hoe ver je op een trajectlijn kunt zijn opgeschoven sinds de vorige tik.
 *
 * 50 m/s is 180 km/u, en dat is ruim boven alles wat op een Nederlandse weg
 * gebeurt — dit is geen snelheidsgrens maar een grens aan wat geloofwaardig is.
 * Uit de verstreken tijd en niet vast, omdat een gat in de GPS legitiem is: na
 * de vijftien seconden van SECTION_EXIT_GRACE_MS mag je 810 meter verder zijn,
 * en dat moet erdoor. Een sprong van 200 meter in één seconde niet.
 *
 * De ruismarge is 25 meter en níet de 60 van SECTION_ON_ROAD_M, al lag dat
 * voor de hand. Die 60 is de *zijdelingse* afstand tot de weg; dit is ruis
 * *langs* de lijn, en dat is een andere grootheid en een kleinere.
 *
 * Het getal is gemeten en niet gekozen. Op de echte A10-lijn
 * (`osm-rel-7219393-1`, 48 punten) blijven er met 60 meter twee verkeerde
 * projecties over, tot 55 meter achteruit; vanaf 40 meter zijn het er nul,
 * zonder dat er ook maar één geldige fix wordt geweigerd. 25 zit daar ruim
 * onder en ruim boven de 5 à 15 meter die GPS langs een lijn afwijkt.
 */
// Meter per seconde, niet milliseconden — vandaar de naam voluit. Hij heette
// eerst MAX_MS, en dat leest als een tijd terwijl het een snelheid is.
export const MAX_M_PER_S = 50;
export const SPRONG_RUIS_M = 25;

/**
 * Het venster waarbinnen de volgende offset mag liggen.
 *
 * Asymmetrisch, en dat is het punt. Vooruit mag je zo ver als 180 km/u in de
 * verstreken tijd toelaat. Achteruit mag je alleen de ruis — want op een
 * traject rijd je één kant op, pickSection eist al dat je koers met de lijn
 * meeloopt, en honderd meter achteruit in een seconde is geen auto maar een
 * projectie die naar een ander stuk van dezelfde lijn is gesprongen.
 *
 * Symmetrisch was niet genoeg: gemeten op de A10-lijn bleven er met een venster
 * van 110 meter twee sprongen over, tot 90 meter achteruit.
 */
export const sprongVenster = (offsetM, verstrekenMs) => ({
  offsetM,
  vooruitM: SPRONG_RUIS_M + (Math.max(0, verstrekenMs) / 1000) * MAX_M_PER_S,
  achteruitM: SPRONG_RUIS_M,
});

/**
 * Een lopende meting. Plat object, geen klasse: dit gaat door React-state heen
 * en moet vergelijkbaar en serialiseerbaar blijven.
 *
 * `startedAtM` is waar je het traject op reed. Meestal nul — maar niet als je
 * er halverwege invoegt, en dat is precies het geval waarvoor dit veld er is.
 */
export const beginRun = ({ reportId, limitKmh, lengthM, offsetM, atMs }) => ({
  reportId,
  limitKmh: limitKmh ?? null,
  lengthM,
  startedAtM: offsetM,
  startedAtMs: atMs,
  lastOffsetM: offsetM,
  lastMs: atMs,
});

/** Meters die je werkelijk binnen het traject hebt afgelegd. */
export const travelledM = (run) => Math.abs(run.lastOffsetM - run.startedAtM);

/** Hoeveel van het stuk nog vóór je ligt. */
export const remainingM = (run, totalM = run.lengthM) =>
  Math.max(0, totalM - Math.max(run.lastOffsetM, run.startedAtM));

/**
 * Het gemiddelde over de afgelegde grond, of null zolang dat er te weinig is.
 *
 * De eerste paar seconden leveren nog geen bruikbaar getal op. Na drie
 * seconden en vijftig meter blijft hetzelfde gemiddelde zichtbaar tot het
 * traject werkelijk verlaten is.
 */
export function averageKmh(run, minMetres = MIN_METRES) {
  const seconds = (run.lastMs - run.startedAtMs) / 1000;
  if (travelledM(run) < minMetres || seconds < MIN_SECONDS) return null;
  return Math.round((travelledM(run) / seconds) * 3.6);
}

/**
 * Of deze meting natuurkundig onmogelijk is, en haar nulpunt dus niet klopt.
 *
 * Het venster op de projectie bewaakt elke volgénde fix, maar niet de eerste:
 * `beginRun` neemt wat er dan uitkomt als nulpunt, en er is nog geen vorige
 * offset om het tegen te houden. Stap je de sectie precies bij de vouw binnen,
 * dan is dat nulpunt honderden meters mis — en omdat `travelledM` de absolute
 * waarde van `laatste − start` is, groeit de afgelegde weg daarna veel sneller
 * dan de tijd.
 *
 * Gemeten op de A10-lijn met GPS elke seconde, rijdend met 75 km/u: zonder
 * venster kwam er tot 1248 km/u op het scherm, met venster nog 131. Met deze
 * controle erbij blijft het 75.
 *
 * Dezelfde grens als het venster: 180 km/u over de hele meting is geen auto op
 * een Nederlandse weg. Komt het daarboven, dan is de meting niet te redden en
 * begint ze opnieuw op de huidige plek. Even geen getal is beter dan een getal
 * dat niet kan.
 */
export const ongeloofwaardig = (run) => {
  const seconden = (run.lastMs - run.startedAtMs) / 1000;
  if (seconden <= 0) return false;
  return travelledM(run) / seconden > MAX_M_PER_S;
};

/** Waar zodra het gemiddelde de limiet met meer dan de marge overschrijdt. */
export function isOver(run, toleranceKmh) {
  const avg = averageKmh(run);
  if (avg == null || run.limitKmh == null) return false;
  return avg > run.limitKmh + toleranceKmh;
}

/**
 * Hoe hard je de rest nog mag rijden om op de limiet uit te komen.
 *
 * Dit is het getal waar een trajectcontrole om draait. Je gemiddelde weten is
 * aardig; weten wat je nog moet doen om het recht te trekken is bruikbaar.
 *
 * De rekensom is die van de controle zelf, achterstevoren. Over het gemeten
 * stuk hoort een reistijd van lengte gedeeld door limiet, en dat is een
 * ondergrens: je moet er minstens zo lang over doen. Wat je daarvan al hebt
 * opgemaakt is weg, en de tijd die overblijft moet je over de resterende
 * meters uitsmeren.
 *
 * Daaruit volgt wat een trajectcontrole wezenlijk anders maakt dan een
 * flitspaal: je kunt hem altijd nog rechttrekken. Tijd loopt maar één kant op,
 * dus langzamer rijden brengt het gemiddelde altijd omlaag. Er is geen punt
 * waarop het te laat is — alleen een punt waarop het advies onaangenaam wordt.
 *
 * Null betekent dat er niets te adviseren valt: geen limiet bekend, het
 * traject is voorbij, of je hebt de voorgeschreven tijd al opgemaakt.
 *
 * Zolang je gemiddelde nog onder de limiet ligt, komt hier een getal uit dat
 * erbóven ligt. Daarom toont het scherm dit alleen als je erover zit: een app
 * die "je mag nog 150" op een honderdweg zet, geeft een advies dat niemand mag
 * opvolgen.
 */
export function allowedRestKmh(run) {
  if (run.limitKmh == null) return null;
  const left = remainingM(run);
  if (left <= 0) return null;
  const measured = Math.max(1, run.lengthM - run.startedAtM);
  const minTotalSeconds = measured / (run.limitKmh / 3.6);
  const usedSeconds = (run.lastMs - run.startedAtMs) / 1000;
  if (usedSeconds < 0) return null;
  const minLeftSeconds = minTotalSeconds - usedSeconds;
  if (minLeftSeconds <= 0) return null;
  return Math.round((left / minLeftSeconds) * 3.6);
}

/**
 * Welk traject je nu op rijdt, uit alles wat de server stuurde.
 *
 * Twee eisen, en de tweede is de reden dat dit niet één regel is:
 *
 *   1. Je ligt binnen 60 m van de lijn.
 *   2. Je rijdt de kant op die de lijn op ligt.
 *
 * Zonder die tweede zit je op de A10 richting noord ook "in" de zuidbaan die
 * er twintig meter naast ligt — en de server stuurt die mee zodra hij je koers
 * niet kent. Gemeten op de echte data: de lijnen van "A10 rechts" lopen op 4°
 * en 2°, die van "A10 links" op 178° en 183°. De lijn zelf zegt dus welke
 * rijbaan het is, en `_orient_line` in de backend legt hem met opzet in de
 * rijrichting.
 *
 * Zonder koers (stilstaand, of een toestel zonder kompas) laten we alle
 * kandidaten toe en wint de dichtstbijzijnde — een gemiddelde over de
 * verkeerde rijbaan is beter dan geen enkel gemiddelde, zolang je maar niet
 * doet alsof je het zeker weet.
 */
export function pickSection(items, lat, lon, heading, maxAlignDeg = 60,
                            preferredReportId = null, venster = null) {
  let best = null;
  let preferred = null;
  for (const m of items || []) {
    const line = sectionLine(m.section);
    if (!line) continue;
    // Het venster geldt alleen voor het traject waar je al op reed. Voor een
    // nieuw traject is er geen vorige offset, en dan is het dichtstbijzijnde
    // punt het enige dat we hebben.
    const hit = projectOnPolyline(lat, lon, line,
                                  m.id === preferredReportId ? venster : null);
    if (!hit || hit.distanceM > SECTION_ON_ROAD_M) continue;
    if (heading != null) {
      const diff = Math.abs(((hit.bearing - heading + 540) % 360) - 180);
      if (diff > maxAlignDeg) continue;
    }
    const candidate = { item: m, hit, line };
    if (m.id === preferredReportId) preferred = candidate;
    if (!best || hit.distanceM < best.hit.distanceM) best = candidate;
  }
  return preferred || best;
}

/**
 * De toestandsmachine: erop, eraf, of bijwerken.
 *
 * Zuiver — hij krijgt de vorige toestand en geeft de nieuwe terug, zodat de
 * hele module te testen is zonder auto, GPS of scherm. Dat is precies wat er
 * op Android níét kan: daar zit dit in DriveService, tussen een
 * locatieluisteraar en een spraakmotor, en is het nergens getest.
 */
export function volgTraject(vorige, items, { lat, lon, heading, nowMs }) {
  // Keep the same relation while it still matches. Overlapping OSM relations
  // otherwise trade places on every GPS tick and reset the average to a dash.
  //
  // En met een venster rond waar je net was, zodat een trajectlijn die langs
  // zichzelf loopt de offset niet honderden meters laat springen. Zie de kop
  // van projectOnPolyline voor de meting die dit afdwong.
  const venster = vorige
    ? sprongVenster(vorige.lastOffsetM, nowMs - vorige.lastMs)
    : null;
  const gekozen = pickSection(items, lat, lon, heading, 60, vorige?.reportId, venster);

  if (!gekozen) {
    // One or two noisy GPS fixes must not restart a run halfway through.
    if (vorige && nowMs - vorige.lastMs <= SECTION_EXIT_GRACE_MS) return vorige;
    return null;
  }

  const { item, hit, line } = gekozen;
  // De exacte lengte als de server hem meestuurt, en anders de lijn zelf. De
  // server verdunt de lijn tot 48 punten vóór verzending, dus zijn eigen
  // `section_length_m` is nauwkeuriger dan wat wij eruit kunnen meten.
  const lengthM = item.section_length_m || polylineLength(line);
  const limitKmh = Number.parseInt(item.maxspeed, 10);

  if (!vorige || vorige.reportId !== item.id) {
    return beginRun({
      reportId: item.id,
      limitKmh: Number.isFinite(limitKmh) ? limitKmh : null,
      lengthM,
      offsetM: hit.offsetM,
      atMs: nowMs,
    });
  }
  const bijgewerkt = { ...vorige, lastOffsetM: hit.offsetM, lastMs: nowMs };
  // Het nulpunt kan mis zijn — zie ongeloofwaardig(). Dan is er niets te
  // repareren aan deze meting en begint ze hier opnieuw.
  if (!ongeloofwaardig(bijgewerkt)) return bijgewerkt;
  return beginRun({
    reportId: item.id,
    limitKmh: Number.isFinite(limitKmh) ? limitKmh : null,
    lengthM,
    offsetM: hit.offsetM,
    atMs: nowMs,
  });
}
